<template>
  <div class="min-h-screen flex items-center justify-center px-4 py-8">
    <UCard class="w-full max-w-2xl">
      <template #header>
        <div class="text-center">
          <!-- Link voltar ao login (apenas no passo 1) -->
          <div v-if="currentStep === 1" class="flex justify-start mb-1">
            <NuxtLink
              to="/auth/login"
              class="flex items-center gap-1 text-xs text-gray-400 hover:text-primary-500 transition-colors"
            >
              <UIcon name="i-heroicons-arrow-left" class="w-3 h-3" />
              Já tenho uma conta
            </NuxtLink>
          </div>
          <h1 class="text-2xl font-bold text-gray-900">Configuração Inicial</h1>
          <p class="mt-1 text-sm text-gray-500">Passo {{ currentStep }} de {{ TOTAL_STEPS }}</p>
          <UProgress :value="(currentStep / TOTAL_STEPS) * 100" class="mt-3" color="primary" />
          <div class="flex justify-between mt-3 px-1">
            <span
              v-for="(label, i) in STEP_LABELS"
              :key="i"
              class="text-xs transition-colors"
              :class="currentStep >= i + 1 ? 'text-primary-600 font-semibold' : 'text-gray-400'"
            >
              {{ label }}
            </span>
          </div>
        </div>
      </template>

      <form @submit.prevent="handleNext" class="space-y-4">

        <!-- Passo 1: Dados da Clínica -->
        <div v-if="currentStep === 1">
          <h3 class="text-lg font-semibold mb-4">Dados da Clínica</h3>
          <UFormGroup label="Nome da Clínica *" :error="errors.name" class="mb-3">
            <UInput v-model="form.name" placeholder="Ex: Clínica Bem-Estar" autofocus @input="onNameInput" />
          </UFormGroup>

          <!-- Endereço / subdomínio -->
          <UFormGroup label="Endereço de acesso *" :error="errors.slug" class="mb-3">
            <template #hint>
              <span class="text-xs text-gray-400">URL única da sua clínica</span>
            </template>
            <div class="flex items-center gap-0">
              <UInput
                :model-value="form.slug"
                placeholder="minha-clinica"
                class="rounded-r-none flex-1"
                :trailing-icon="slugStatusIcon"
                :color="slugInputColor"
                @update:model-value="onSlugChange"
              />
              <span
                class="inline-flex items-center px-3 py-2 border border-l-0 rounded-r-lg bg-gray-50 text-gray-500 text-sm whitespace-nowrap"
                style="border-color: rgb(229 231 235); height: 38px"
              >
                .{{ baseDomain }}
              </span>
            </div>
            <div class="mt-1.5 flex items-center gap-1.5 min-h-[18px]">
              <template v-if="slugStatus === 'checking'">
                <UIcon name="i-heroicons-arrow-path" class="w-3.5 h-3.5 text-gray-400 animate-spin" />
                <span class="text-xs text-gray-400">Verificando disponibilidade…</span>
              </template>
              <template v-else-if="slugStatus === 'available'">
                <UIcon name="i-heroicons-check-circle" class="w-3.5 h-3.5 text-green-500" />
                <span class="text-xs text-green-600">Disponível — <strong>{{ form.slug }}.{{ baseDomain }}</strong></span>
              </template>
              <template v-else-if="slugStatus === 'taken'">
                <UIcon name="i-heroicons-x-circle" class="w-3.5 h-3.5 text-red-500" />
                <span class="text-xs text-red-600">
                  Já utilizado.
                  <button v-if="slugSuggested" type="button" class="underline font-medium" @click="applySuggestion">
                    Usar "{{ slugSuggested }}"
                  </button>
                </span>
              </template>
              <template v-else-if="slugStatus === 'error'">
                <UIcon name="i-heroicons-exclamation-triangle" class="w-3.5 h-3.5 text-yellow-500" />
                <span class="text-xs text-yellow-600">Não foi possível verificar — o endereço será validado ao finalizar</span>
              </template>
            </div>
          </UFormGroup>

          <UFormGroup label="CNPJ" :error="errors.cnpj" hint="Opcional para autônomos" class="mb-3">
            <UInput :value="form.cnpj" placeholder="00.000.000/0000-00" @input="onCnpjInput" />
          </UFormGroup>
          <div class="grid grid-cols-2 gap-3">
            <UFormGroup label="Telefone" :error="errors.phone">
              <UInput v-model="form.phone" placeholder="(11) 9 9999-9999" />
            </UFormGroup>
            <UFormGroup label="E-mail da Clínica" :error="errors.email">
              <UInput v-model="form.email" type="email" placeholder="contato@clinica.com" />
            </UFormGroup>
          </div>
        </div>

        <!-- Passo 2: Dados do Responsável -->
        <div v-else-if="currentStep === 2">
          <h3 class="text-lg font-semibold mb-4">Dados do Responsável</h3>
          <UFormGroup label="Nome completo *" :error="errors.admin_name" class="mb-3">
            <UInput v-model="form.admin_name" placeholder="Maria da Silva" />
          </UFormGroup>
          <UFormGroup label="E-mail *" :error="errors.admin_email" class="mb-3">
            <UInput v-model="form.admin_email" type="email" placeholder="admin@clinica.com" />
          </UFormGroup>
          <div class="grid grid-cols-2 gap-3">
            <UFormGroup label="Senha *" :error="errors.admin_password">
              <UInput
                v-model="form.admin_password"
                :type="showAdminPassword ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="Mín. 8 caracteres"
                @input="clearErrors()"
              >
                <template #trailing>
                  <button type="button" tabindex="-1" class="flex items-center justify-center p-1" @click="showAdminPassword = !showAdminPassword">
                    <UIcon :name="showAdminPassword ? 'i-heroicons-eye-slash' : 'i-heroicons-eye'" class="text-gray-400 hover:text-gray-600 w-4 h-4" />
                  </button>
                </template>
              </UInput>
              <div class="mt-1">
                <div class="flex gap-0.5">
                  <div
                    v-for="i in 4"
                    :key="i"
                    class="h-1 flex-1 rounded-full transition-colors"
                    :class="passwordStrengthColor(i, form.admin_password)"
                  />
                </div>
                <p class="text-xs text-gray-400 mt-0.5">
                  {{ passwordStrengthLabel(form.admin_password) }}
                </p>
              </div>
            </UFormGroup>
            <UFormGroup label="Confirmar senha *" :error="errors.admin_password_confirm">
              <UInput
                v-model="form.admin_password_confirm"
                :type="showAdminPassword ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="Repita a senha"
              >
                <template #trailing>
                  <button type="button" tabindex="-1" class="flex items-center justify-center p-1" @click="showAdminPassword = !showAdminPassword">
                    <UIcon :name="showAdminPassword ? 'i-heroicons-eye-slash' : 'i-heroicons-eye'" class="text-gray-400 hover:text-gray-600 w-4 h-4" />
                  </button>
                </template>
              </UInput>
            </UFormGroup>
          </div>
        </div>

        <!-- Passo 3: Configurações da Agenda -->
        <div v-else-if="currentStep === 3">
          <h3 class="text-lg font-semibold mb-4">Configurações da Agenda</h3>
          <div class="grid grid-cols-2 gap-3 mb-3">
            <UFormGroup label="Horário de abertura *" :error="errors.opening_time">
              <UInput v-model="form.opening_time" type="time" />
            </UFormGroup>
            <UFormGroup label="Horário de fechamento *" :error="errors.closing_time">
              <UInput v-model="form.closing_time" type="time" />
            </UFormGroup>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <UFormGroup label="Duração padrão (minutos) *" :error="errors.default_duration">
              <UInput v-model.number="form.default_duration" type="number" min="15" max="480" />
            </UFormGroup>
            <UFormGroup label="Cancelamento com antecedência (horas) *" :error="errors.cancel_hours">
              <UInput v-model.number="form.cancel_hours" type="number" min="0" />
            </UFormGroup>
          </div>
        </div>

        <!-- Passo 4: Convidar Equipe -->
        <div v-else>
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-lg font-semibold">Convidar Equipe</h3>
            <UBadge color="gray" variant="outline">Opcional</UBadge>
          </div>
          <p class="text-sm text-gray-500 mb-4">
            Adicione profissionais que terão acesso à plataforma. Você pode fazer isso depois em
            <strong>Configurações �?' Usuários</strong>.
          </p>

          <div
            v-if="invites.length === 0"
            class="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg mb-3"
          >
            <UIcon name="i-heroicons-user-plus" class="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p class="text-sm text-gray-400">Nenhum profissional adicionado ainda</p>
          </div>

          <div
            v-for="(invite, idx) in invites"
            :key="idx"
            class="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50"
          >
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-gray-600">Profissional {{ idx + 1 }}</span>
              <UButton
                type="button"
                color="red"
                variant="ghost"
                icon="i-heroicons-x-mark"
                size="xs"
                @click="invites.splice(idx, 1)"
              />
            </div>
            <UFormGroup label="Nome completo *" :error="invite.nameError" class="mb-2">
              <UInput v-model="invite.name" placeholder="Nome do profissional" size="sm" />
            </UFormGroup>
            <div class="grid grid-cols-2 gap-2">
              <UFormGroup label="E-mail *" :error="invite.emailError">
                <UInput v-model="invite.email" type="email" placeholder="email@clinica.com" size="sm" />
              </UFormGroup>
              <UFormGroup label="Função *" :error="invite.roleError">
                <USelect v-model="invite.role" :options="ROLE_OPTIONS" size="sm" />
              </UFormGroup>
            </div>
          </div>

          <UButton
            type="button"
            variant="outline"
            color="gray"
            icon="i-heroicons-plus"
            block
            @click="addInvite"
          >
            Adicionar profissional
          </UButton>
        </div>

        <!-- Navegação -->
        <div class="flex gap-3 pt-2">
          <UButton
            v-if="currentStep > 1"
            type="button"
            color="gray"
            variant="outline"
            icon="i-heroicons-arrow-left"
            :disabled="loading"
            @click="currentStep--"
          >
            Voltar
          </UButton>
          <UButton v-if="currentStep < TOTAL_STEPS" type="submit" color="primary" class="flex-1">
            Próximo
            <template #trailing>
              <UIcon name="i-heroicons-arrow-right" class="w-4 h-4" />
            </template>
          </UButton>
          <UButton v-else type="submit" color="primary" class="flex-1" :loading="loading">
            Finalizar e entrar
          </UButton>
        </div>

      </form>
    </UCard>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: 'auth',
  middleware: 'guest',
})

const router = useRouter()
const toast = useToast()
const auth = useAuthStore()
const api = useApi()
const config = useRuntimeConfig()

const baseDomain = computed(() => (config.public.baseDomain as string) || 'localhost')

// Onboarding só existe no schema público. Se estiver em subdomínio, redireciona.
onMounted(() => {
  const bd   = config.public.baseDomain as string
  const host = window.location.hostname
  const isSubdomain = host !== bd && host !== `www.${bd}` && host.endsWith(`.${bd}`)
  if (isSubdomain) {
    window.location.href = `https://${bd}/auth/onboarding`
  }
})

const TOTAL_STEPS = 4
const STEP_LABELS = ['Clínica', 'Responsável', 'Agenda', 'Equipe']
const ROLE_OPTIONS = [
  { label: 'Psicólogo(a)', value: 'professional' },
  { label: 'Recepcionista', value: 'reception' },
  { label: 'Administrador', value: 'admin' },
]
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const currentStep = ref(1)
const loading = ref(false)
const showAdminPassword = ref(false)

// ── Slug / subdomínio ─────────────────────────────────────────────────────────
type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error'
const slugStatus    = ref<SlugStatus>('idle')
const slugSuggested = ref<string | null>(null)
let slugTimer: ReturnType<typeof setTimeout> | null = null

function jsSlugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

const slugStatusIcon = computed(() => {
  if (slugStatus.value === 'available') return 'i-heroicons-check-circle'
  if (slugStatus.value === 'taken')     return 'i-heroicons-x-circle'
  return undefined
})

const slugInputColor = computed(() => {
  if (slugStatus.value === 'available') return 'green'
  if (slugStatus.value === 'taken')     return 'red'
  return 'white'
})

async function checkSlug(slug: string) {
  if (!slug) { slugStatus.value = 'idle'; return }
  slugStatus.value = 'checking'
  try {
    const { data } = await api.get(`/auth/check-slug?slug=${encodeURIComponent(slug)}`)
    const d = data?.data
    if (d?.available) {
      slugStatus.value = 'available'
      slugSuggested.value = null
    } else {
      slugStatus.value = 'taken'
      slugSuggested.value = d?.suggested ?? null
    }
  } catch {
    slugStatus.value = 'error'
  }
}

function scheduleSlugCheck(slug: string) {
  if (slugTimer) clearTimeout(slugTimer)
  slugStatus.value = slug ? 'checking' : 'idle'
  slugTimer = setTimeout(() => checkSlug(slug), 500)
}

function onNameInput() {
  const candidate = jsSlugify(form.name)
  form.slug = candidate
  scheduleSlugCheck(candidate)
}

function onSlugChange(val: string) {
  form.slug = jsSlugify(val)
  scheduleSlugCheck(form.slug)
}

function applySuggestion() {
  if (slugSuggested.value) {
    form.slug = slugSuggested.value
    scheduleSlugCheck(form.slug)
  }
}

const form = reactive({
  name: '',
  slug: '',
  cnpj: '',
  phone: '',
  email: '',
  admin_name: '',
  admin_email: '',
  admin_password: '',
  admin_password_confirm: '',
  opening_time: '08:00',
  closing_time: '18:00',
  default_duration: 50,
  cancel_hours: 24,
})

const errors = reactive<Record<string, string>>({})

interface Invite {
  name: string
  email: string
  role: string
  nameError: string
  emailError: string
  roleError: string
}

const invites = reactive<Invite[]>([])

function addInvite() {
  invites.push({ name: '', email: '', role: 'professional', nameError: '', emailError: '', roleError: '' })
}

function passwordScore(pw: string): number {
  let score = 0
  if (pw.length >= 8) score++
  if (pw.length >= 12) score++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++
  if (/\d/.test(pw)) score++
  if (/[^a-zA-Z0-9]/.test(pw)) score++
  return Math.min(score, 4)
}

function passwordStrengthColor(barIndex: number, pw: string): string {
  const score = passwordScore(pw)
  if (!pw) return 'bg-gray-200'
  if (barIndex > score) return 'bg-gray-200'
  if (score <= 1) return 'bg-red-400'
  if (score === 2) return 'bg-orange-400'
  if (score === 3) return 'bg-yellow-400'
  return 'bg-green-400'
}

function passwordStrengthLabel(pw: string): string {
  if (!pw) return ''
  const score = passwordScore(pw)
  if (score <= 1) return 'Fraca'
  if (score === 2) return 'Média'
  if (score === 3) return 'Boa'
  return 'Forte'
}

function onCnpjInput(e: Event) {
  let v = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 14)
  v = v
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3})(\d)/, '$1/$2')
    .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{4})(\d)/, '$1-$2')
  form.cnpj = v
}

function clearErrors() {
  Object.keys(errors).forEach(k => delete errors[k])
}

function validateStep(): boolean {
  clearErrors()
  let valid = true

  if (currentStep.value === 1) {
    if (!form.name.trim()) {
      errors.name = 'Nome da clínica é obrigatório'
      valid = false
    }
    if (!form.slug) {
      errors.slug = 'Endereço de acesso é obrigatório'
      valid = false
    } else if (slugStatus.value === 'taken') {
      errors.slug = 'Este endereço já está em uso — escolha outro ou use a sugestão'
      valid = false
    } else if (slugStatus.value === 'checking') {
      errors.slug = 'Aguarde a verificação de disponibilidade'
      valid = false
    } else if (slugStatus.value === 'idle' && form.slug) {
      // Ainda não verificou: dispara a checagem e deixa passar
      // (backend validará no submit)
      scheduleSlugCheck(form.slug)
    }
    const digits = form.cnpj.replace(/\D/g, '')
    if (digits && digits.length !== 14) {
      errors.cnpj = 'CNPJ deve ter 14 dígitos'
      valid = false
    }
    if (form.email && !EMAIL_RE.test(form.email)) {
      errors.email = 'E-mail inválido'
      valid = false
    }
  }

  if (currentStep.value === 2) {
    if (!form.admin_name.trim()) {
      errors.admin_name = 'Nome é obrigatório'
      valid = false
    }
    if (!form.admin_email) {
      errors.admin_email = 'E-mail é obrigatório'
      valid = false
    } else if (!EMAIL_RE.test(form.admin_email)) {
      errors.admin_email = 'E-mail inválido'
      valid = false
    }
    if (!form.admin_password) {
      errors.admin_password = 'Senha é obrigatória'
      valid = false
    } else if (form.admin_password.length < 8) {
      errors.admin_password = 'Mínimo de 8 caracteres'
      valid = false
    }
    if (form.admin_password !== form.admin_password_confirm) {
      errors.admin_password_confirm = 'As senhas não conferem'
      valid = false
    }
  }

  if (currentStep.value === 3) {
    if (!form.opening_time) {
      errors.opening_time = 'Obrigatório'
      valid = false
    }
    if (!form.closing_time) {
      errors.closing_time = 'Obrigatório'
      valid = false
    } else if (form.opening_time && form.opening_time >= form.closing_time) {
      errors.closing_time = 'Deve ser após o horário de abertura'
      valid = false
    }
    if (!form.default_duration || form.default_duration < 15) {
      errors.default_duration = 'Mínimo de 15 minutos'
      valid = false
    }
    if (form.cancel_hours < 0) {
      errors.cancel_hours = 'Não pode ser negativo'
      valid = false
    }
  }

  if (currentStep.value === 4) {
    for (const invite of invites) {
      invite.nameError = ''
      invite.emailError = ''
      invite.roleError = ''
      if (!invite.name.trim()) { invite.nameError = 'Obrigatório'; valid = false }
      if (!invite.email) { invite.emailError = 'Obrigatório'; valid = false }
      else if (!EMAIL_RE.test(invite.email)) { invite.emailError = 'E-mail inválido'; valid = false }
      if (!invite.role) { invite.roleError = 'Selecione uma função'; valid = false }
    }
  }

  return valid
}

function handleNext() {
  if (!validateStep()) return
  if (currentStep.value < TOTAL_STEPS) {
    currentStep.value++
    return
  }
  handleFinish()
}

async function handleFinish() {
  loading.value = true
  try {
    const { data } = await api.post('/auth/onboarding', {
      clinic: {
        name: form.name,
        slug: form.slug,
        cnpj: form.cnpj,
        phone: form.phone,
        email: form.email,
        opening_time: form.opening_time,
        closing_time: form.closing_time,
        default_duration: form.default_duration,
        cancel_hours: form.cancel_hours,
      },
      admin: {
        name: form.admin_name,
        email: form.admin_email,
        password: form.admin_password,
        password_confirm: form.admin_password_confirm,
      },
      invites: invites.map(i => ({ name: i.name, email: i.email, role: i.role })),
    })

    auth.setTokens(data.data.tokens)
    auth.user = data.data.user
    if (data.data.tenant?.slug) auth.setTenant(data.data.tenant.slug)

    toast.add({
      title: 'Clínica criada com sucesso!',
      description: `Bem-vindo(a), ${form.admin_name.split(' ')[0]}!`,
      color: 'green',
    })
    router.push('/app/dashboard')
  } catch (err: any) {
    const errBody = err.response?.data?.error ?? err.data?.error
    const details = errBody?.details
    if (errBody?.suggested_slug) {
      // Slug duplicado — volta ao passo 1 e aplica a sugestão
      slugStatus.value = 'taken'
      slugSuggested.value = errBody.suggested_slug
      errors.slug = `"${form.slug}" já está em uso — use a sugestão abaixo`
      currentStep.value = 1
    } else if (details?.clinic?.cnpj) {
      errors.cnpj = Array.isArray(details.clinic.cnpj) ? details.clinic.cnpj[0] : details.clinic.cnpj
      currentStep.value = 1
    } else if (details?.admin?.email) {
      errors.admin_email = Array.isArray(details.admin.email) ? details.admin.email[0] : details.admin.email
      currentStep.value = 2
    }
    toast.add({
      title: 'Erro ao criar clínica',
      description: errBody?.message || 'Verifique os dados e tente novamente.',
      color: 'red',
    })
  } finally {
    loading.value = false
  }
}
</script>

